// ./src/hooks/useDisplayRunningMessages.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  DisplayRunningMessage,
  DisplayRunningMessageService,
  DisplayRunningMessageFilter,
  DisplayRunningMessageInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseDisplayRunningMessagesOptions {
  /** Display ID to filter running messages by */
  displayId?: string | undefined;

  /** Running Message ID to filter running messages by */
  runningMessageId?: string | undefined;

  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<DisplayRunningMessageFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseDisplayRunningMessagesReturn {
  // Data state
  displayRunningMessage: DisplayRunningMessage | null;
  displayRunningMessages: DisplayRunningMessage[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<DisplayRunningMessageFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingDisplayRunningMessageId: string | null;
  selectedDisplayRunningMessage: DisplayRunningMessage | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<DisplayRunningMessageFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<DisplayRunningMessage[]>;
  create: (
    params: DisplayRunningMessageInput
  ) => Promise<DisplayRunningMessage | null>;
  update: (
    id: string,
    params: DisplayRunningMessageInput
  ) => Promise<DisplayRunningMessage | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<DisplayRunningMessageFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedDisplayRunningMessage: (
    displayRunningMessage: DisplayRunningMessage | null
  ) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  displayRunningMessagesByDisplay: Record<string, DisplayRunningMessage[]>;
  displayRunningMessagesByRunningMessage: Record<
    string,
    DisplayRunningMessage[]
  >;
  activeDisplayRunningMessages: DisplayRunningMessage[];
  scheduledDisplayRunningMessages: DisplayRunningMessage[];

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "orderNo", direction: "asc" }];

export const useDisplayRunningMessages = (
  options: UseDisplayRunningMessagesOptions = {}
): UseDisplayRunningMessagesReturn => {
  const {
    displayId,
    runningMessageId,
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Use refs for option values to avoid dependency issues
  const displayIdRef = useRef(displayId);
  const runningMessageIdRef = useRef(runningMessageId);
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    displayIdRef.current = displayId;
  }, [displayId]);

  useEffect(() => {
    runningMessageIdRef.current = runningMessageId;
  }, [runningMessageId]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [displayRunningMessage, setDisplayRunningMessage] =
    useState<DisplayRunningMessage | null>(null);
  const [displayRunningMessages, setDisplayRunningMessages] = useState<
    DisplayRunningMessage[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<DisplayRunningMessageFilter>>(
    () => {
      const filter = { ...initialFilters };
      if (displayId) {
        filter.displayId = [displayId];
      }
      if (runningMessageId) {
        filter.runningMessageId = [runningMessageId];
      }
      return filter;
    }
  );
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingDisplayRunningMessageId, setRemovingDisplayRunningMessageId] =
    useState<string | null>(null);
  const [selectedDisplayRunningMessage, setSelectedDisplayRunningMessage] =
    useState<DisplayRunningMessage | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => displayRunningMessages.length === 0 && !loading,
    [displayRunningMessages.length, loading]
  );

  const displayRunningMessagesByDisplay = useMemo(() => {
    return displayRunningMessages.reduce((acc, displayRunningMessage) => {
      const displayId = displayRunningMessage.displayId || "unknown";
      if (!acc[displayId]) {
        acc[displayId] = [];
      }
      acc[displayId].push(displayRunningMessage);
      return acc;
    }, {} as Record<string, DisplayRunningMessage[]>);
  }, [displayRunningMessages]);

  const displayRunningMessagesByRunningMessage = useMemo(() => {
    return displayRunningMessages.reduce((acc, displayRunningMessage) => {
      const runningMessageId =
        displayRunningMessage.runningMessageId || "unknown";
      if (!acc[runningMessageId]) {
        acc[runningMessageId] = [];
      }
      acc[runningMessageId].push(displayRunningMessage);
      return acc;
    }, {} as Record<string, DisplayRunningMessage[]>);
  }, [displayRunningMessages]);

  const activeDisplayRunningMessages = useMemo(() => {
    const now = new Date().toISOString();
    return displayRunningMessages.filter((displayRunningMessage) => {
      const hasStarted =
        !displayRunningMessage.startDatetime ||
        displayRunningMessage.startDatetime <= now;
      const notEnded =
        !displayRunningMessage.endDatetime ||
        displayRunningMessage.endDatetime > now;
      return hasStarted && notEnded;
    });
  }, [displayRunningMessages]);

  const scheduledDisplayRunningMessages = useMemo(() => {
    const now = new Date().toISOString();
    return displayRunningMessages.filter((displayRunningMessage) => {
      const notStarted =
        displayRunningMessage.startDatetime &&
        displayRunningMessage.startDatetime > now;
      return notStarted;
    });
  }, [displayRunningMessages]);

  // Helper function to update display running message in list if it exists, or add it if not
  const updateOrAddDisplayRunningMessageInList = useCallback(
    (updatedDisplayRunningMessage: DisplayRunningMessage) => {
      setDisplayRunningMessages((prev) => {
        const existingIndex = prev.findIndex(
          (drm) => drm.id === updatedDisplayRunningMessage.id
        );
        if (existingIndex >= 0) {
          const newDisplayRunningMessages = [...prev];
          newDisplayRunningMessages[existingIndex] =
            updatedDisplayRunningMessage;
          return newDisplayRunningMessages;
        } else {
          return [updatedDisplayRunningMessage, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove display running message from list if it exists
  const removeDisplayRunningMessageFromList = useCallback(
    (displayRunningMessageId: string) => {
      setDisplayRunningMessages((prev) =>
        prev.filter((drm) => drm.id !== displayRunningMessageId)
      );
    },
    []
  );

  // Helper function to remove multiple display running messages from list
  const removeDisplayRunningMessagesFromList = useCallback(
    (displayRunningMessageIds: string[]) => {
      const idsSet = new Set(displayRunningMessageIds);
      setDisplayRunningMessages((prev) =>
        prev.filter((drm) => !idsSet.has(drm.id))
      );
    },
    []
  );

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setDisplayRunningMessage(null);
    setDisplayRunningMessages([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingDisplayRunningMessageId(null);
    setSelectedDisplayRunningMessage(null);
  }, []);

  // Get single display running message (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await DisplayRunningMessageService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const displayRunningMessageData = result
            .data[0] as DisplayRunningMessage;
          setDisplayRunningMessage(displayRunningMessageData);
          updateOrAddDisplayRunningMessageInList(displayRunningMessageData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load display running message";
          setLastError(errorMessage);
          setDisplayRunningMessage(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load display running message";
        setLastError(errorMessage);
        setDisplayRunningMessage(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddDisplayRunningMessageInList]
  );

  // List display running messages (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<DisplayRunningMessageFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setDisplayRunningMessages([]);
          setCurrentPage(1);
        }
      }

      const combinedFilter = { ...filter };
      if (displayIdRef.current && !combinedFilter.displayId) {
        combinedFilter.displayId = [displayIdRef.current];
      }
      if (runningMessageIdRef.current && !combinedFilter.runningMessageId) {
        combinedFilter.runningMessageId = [runningMessageIdRef.current];
      }

      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await DisplayRunningMessageService.list(
          combinedFilter as DisplayRunningMessageFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newDisplayRunningMessages = Array.isArray(result.data)
            ? result.data
            : [];

          if (isAppending) {
            setDisplayRunningMessages((prev) => {
              const existingIds = new Set(prev.map((drm) => drm.id));
              const uniqueNewDisplayRunningMessages =
                newDisplayRunningMessages.filter(
                  (drm) => !existingIds.has(drm.id)
                );
              return [...prev, ...uniqueNewDisplayRunningMessages];
            });
          } else {
            setDisplayRunningMessages(newDisplayRunningMessages);
          }

          setHasMore(newDisplayRunningMessages.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load display running messages";
          setLastError(errorMessage);
          if (!isAppending) setDisplayRunningMessages([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load display running messages";
        setLastError(errorMessage);
        if (!isAppending) setDisplayRunningMessages([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many display running messages by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<DisplayRunningMessage[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await DisplayRunningMessageService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedDisplayRunningMessages = Array.isArray(result.data)
            ? result.data
            : [];
          loadedDisplayRunningMessages.forEach((displayRunningMessage) => {
            updateOrAddDisplayRunningMessageInList(displayRunningMessage);
          });
          setLastError(null);
          return loadedDisplayRunningMessages;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load display running messages";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load display running messages";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddDisplayRunningMessageInList]
  );

  // Create display running message (matching API method name)
  const create = useCallback(
    async (
      params: DisplayRunningMessageInput
    ): Promise<DisplayRunningMessage | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const displayRunningMessageToCreate = {
          ...params,
          displayId: params.displayId || displayIdRef.current || null,
          runningMessageId:
            params.runningMessageId || runningMessageIdRef.current || null,
        };

        const result = await DisplayRunningMessageService.create(
          displayRunningMessageToCreate
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newDisplayRunningMessage = result
            .data[0] as DisplayRunningMessage;
          setDisplayRunningMessage(newDisplayRunningMessage);
          setDisplayRunningMessages((prev) => [
            newDisplayRunningMessage,
            ...prev,
          ]);
          setLastError(null);
          return newDisplayRunningMessage;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create display running message";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create display running message";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update display running message (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: DisplayRunningMessageInput
    ): Promise<DisplayRunningMessage | null> => {
      if (!id) {
        setLastError("Display running message ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await DisplayRunningMessageService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedDisplayRunningMessage = result
            .data[0] as DisplayRunningMessage;
          setDisplayRunningMessage(updatedDisplayRunningMessage);
          updateOrAddDisplayRunningMessageInList(updatedDisplayRunningMessage);
          setLastError(null);
          return updatedDisplayRunningMessage;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update display running message";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update display running message";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddDisplayRunningMessageInList]
  );

  // Remove display running message (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Display running message ID is required for removal");
        return false;
      }

      setRemovingDisplayRunningMessageId(id);
      setLastError(null);

      try {
        const result = await DisplayRunningMessageService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setDisplayRunningMessage(null);
          removeDisplayRunningMessageFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove display running message";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove display running message";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingDisplayRunningMessageId(null);
      }
    },
    [removeDisplayRunningMessageFromList]
  );

  // Remove many display running messages (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError(
          "At least one display running message ID is required for removal"
        );
        return false;
      }

      setLastError(null);

      try {
        const result = await DisplayRunningMessageService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setDisplayRunningMessage(null);
          removeDisplayRunningMessagesFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove display running messages";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove display running messages";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeDisplayRunningMessagesFromList]
  );

  // Load more display running messages
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh display running messages
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      const autoLoadFilters = { ...initialFilters };
      if (displayId) {
        autoLoadFilters.displayId = [displayId];
      }
      if (runningMessageId) {
        autoLoadFilters.runningMessageId = [runningMessageId];
      }

      list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    displayRunningMessage,
    displayRunningMessages,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingDisplayRunningMessageId,
    selectedDisplayRunningMessage,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedDisplayRunningMessage,
    reset,
    displayRunningMessagesByDisplay,
    displayRunningMessagesByRunningMessage,
    activeDisplayRunningMessages,
    scheduledDisplayRunningMessages,
    lastError,
    clearError,
  };
};
